import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { isRevealSuperseded, useRevealEventPassword } from './useRevealEventPassword';
import { subscribeToPasswordChanges } from './revealEventPasswordStore';

export type EventPasswordValue = Readonly<{
  password: string | null;
  /** Not true on error: a failed reveal is not "no password", so gating callers hold instead of writing an empty value. */
  isResolved: boolean;
  isRevealing: boolean;
  error: Error | null;
  passwordRef: MutableRefObject<string | null>;
  ensure: () => Promise<string | null>;
}>;

const toError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)));

export const useEventPasswordValue = (eventId: string | undefined, hasEventPassword: boolean | undefined, options?: Readonly<{ enabled?: boolean }>): EventPasswordValue => {
  const enabled = options?.enabled !== false;
  const { password, isRevealing, reveal } = useRevealEventPassword(eventId);
  const [error, setError] = useState<Error | null>(null);
  // A reveal settled with no error but no password either (e.g. hasEventPassword was stale-true).
  // Tracked separately from `error` so isResolved can be true without pretending something failed.
  const [isSettledNull, setIsSettledNull] = useState(false);
  // Nothing else re-runs the effect: the superseding broadcast's writes are often no-ops.
  const [retryToken, setRetryToken] = useState(0);

  // Purge a stale event's error/settled-null during render, mirroring how useRevealEventPassword purges its value.
  const lastEventIdRef = useRef(eventId);
  if (lastEventIdRef.current !== eventId) {
    lastEventIdRef.current = eventId;
    if (error) setError(null);
    if (isSettledNull) setIsSettledNull(false);
  }

  const shouldReveal = enabled && hasEventPassword === true && !!eventId;

  // A password change broadcast elsewhere must clear either settle state so the reveal effect
  // below runs again instead of hanging resolved forever on a stale null or a stale error.
  useEffect(() => {
    if (!eventId) return undefined;
    return subscribeToPasswordChanges(changedEventId => {
      if (changedEventId !== eventId) return;
      setIsSettledNull(false);
      setError(null);
    });
  }, [eventId]);

  useEffect(() => {
    if (!shouldReveal || password !== null || error || isSettledNull) return undefined;
    let cancelled = false;
    reveal()
      .then(value => {
        if (!cancelled && value === null) setIsSettledNull(true);
      })
      .catch(err => {
        if (cancelled) return;
        if (isRevealSuperseded(err)) setRetryToken(token => token + 1);
        else setError(toError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [shouldReveal, password, error, isSettledNull, reveal, retryToken]);

  const ensure = useCallback(async (): Promise<string | null> => {
    if (!shouldReveal || isSettledNull) return null;
    if (password !== null) return password;
    try {
      const value = await reveal();
      setError(null);
      if (value === null) setIsSettledNull(true);
      return value;
    } catch (err) {
      // Rethrow: callers must not mistake a failed reveal for "no password".
      if (isRevealSuperseded(err)) setRetryToken(token => token + 1);
      else setError(toError(err));
      throw err;
    }
  }, [shouldReveal, isSettledNull, password, reveal]);

  const isResolved = !enabled || hasEventPassword === false || password !== null || isSettledNull;

  const passwordRef = useRef<string | null>(password);
  passwordRef.current = password;

  return { password, isResolved, isRevealing, error, passwordRef, ensure };
};
